/**
 * 告警区。原型这里是六条写死的假告警(还带"3分钟前")。
 *
 * 现在的告警由 hub 每 10 秒判定一次:掉线、低电、高温。三条性质值得记住:
 *  · **同一条件不刷屏** —— 重复只累加次数,所以一条告警会显示"×12";
 *  · **条件消失自动消解** —— 设备回来了,掉线告警自己就没了,不用人去点;
 *  · **没有遥测不报警** —— 设备没报电量就不会有"电量低",而不是把未知当危急。
 * 「确认」是人的动作,和"自己好了"分开记 —— 否则没人知道一条告警是被解决的还是自愈的。
 */
import type { HubAlert } from '@/lib/hubClient';
import { LoadError, NoDataSource } from '@/components/feature/DataSource';
import { timeAgoSec } from '@/lib/format';

const levelStyle: Record<string, { box: string; dot: string; label: string }> = {
  critical: { box: 'bg-red-50 border-red-200/70', dot: 'bg-red-500', label: '严重' },
  warning: { box: 'bg-amber-50 border-amber-200/70', dot: 'bg-amber-500', label: '警告' },
  info: { box: 'bg-background-100 border-background-200/70', dot: 'bg-foreground-300', label: '提示' },
};

const kindLabel: Record<string, string> = {
  device_offline: '设备掉线', battery_low: '电量偏低', temp_high: '温度偏高',
  control_plane_down: '控制面不可达',
};

interface Props {
  alerts: HubAlert[];
  counts: Record<string, number>;
  loading: boolean;
  error: string | null;
  unavailable: string | null;
  ack: (id: number) => Promise<void>;
  refetch: () => void;
}

export default function AlertArea({ alerts, counts, loading, error, unavailable, ack, refetch }: Props) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground-800 font-heading">告警</h3>
        <div className="flex items-center gap-3 text-[11px]">
          {(['critical', 'warning', 'info'] as const).map((lvl) => (
            <span key={lvl} className="flex items-center gap-1 text-foreground-500">
              <span className={`w-1.5 h-1.5 rounded-full ${levelStyle[lvl].dot}`} />
              {levelStyle[lvl].label} {counts[lvl] ?? 0}
            </span>
          ))}
        </div>
      </div>

      {error && <LoadError error={error} onRetry={refetch} />}

      {unavailable ? (
        <NoDataSource title="告警功能未配置" reason={unavailable} phase="给 hub 配上 fleet.db 即可" />
      ) : loading && alerts.length === 0 ? (
        <p className="text-xs text-foreground-400 py-6 text-center">读取中…</p>
      ) : alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <span className="w-12 h-12 rounded-full bg-accent-50 text-accent-600 flex items-center justify-center mb-2">
            <i className="ri-shield-check-line text-xl" />
          </span>
          <p className="text-sm font-medium text-foreground-600">当前没有活跃告警</p>
          <p className="text-[11px] text-foreground-400 mt-1 max-w-sm leading-relaxed">
            hub 每 10 秒判定一次掉线 / 低电 / 高温。这里是空的,说明**判定跑过而且没命中** ——
            和"没有告警功能"不是一回事,后者会在这块显示"未配置"。
          </p>
        </div>
      ) : (
        <ul className="space-y-2 max-h-[320px] overflow-y-auto">
          {alerts.map((a) => {
            const st = levelStyle[a.level] ?? levelStyle.info;
            return (
              <li key={a.id} className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border ${st.box}`}>
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${st.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-foreground-800">{a.message || kindLabel[a.kind] || a.kind}</span>
                    {a.count > 1 && <span className="text-[10px] font-mono text-foreground-400">×{a.count}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2.5 text-[10px] text-foreground-500 mt-0.5">
                    <span>{kindLabel[a.kind] ?? a.kind}</span>
                    <span className="font-mono">{a.source}</span>
                    <span>首次 {timeAgoSec(a.firstAt)}</span>
                    <span>最近 {timeAgoSec(a.lastAt)}</span>
                  </div>
                </div>
                <button onClick={() => ack(a.id)}
                  className="shrink-0 text-[11px] px-2 py-1 rounded border border-background-300/70 text-foreground-600 hover:bg-background-50 cursor-pointer whitespace-nowrap">
                  确认
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
